import { useRegions } from '@terminal/react-query-hooks/geographies/useRegions';
import { useFollowedTopics } from '@terminal/react-query-hooks/topics/useFollowedTopics';
import { useFollowTopic } from '@terminal/react-query-hooks/topics/useFollowTopic';
import { useUnfollowTopic } from '@terminal/react-query-hooks/topics/useUnfollowTopic';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { FollowGeographiesSearchBar } from '@terminal/views/onboarding/follow-geographies/follow-geographies-search-bar/FollowGeographiesSearchBar';
import { useMemo, useState } from 'react';
import styles from './ManageFollowingGeographiesTab.module.scss';

export const ManageFollowingGeographiesTab = ({
  trackingSource,
}: {
  trackingSource: PosthogTrackingSource;
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { trackFollowGeography, trackUnfollowGeography } = usePosthogEvents();
  const { data: regionsData } = useRegions(true);
  const { data: followedTopicsData } = useFollowedTopics('geography');

  const { mutate: followTopic } = useFollowTopic();
  const { mutate: unfollowTopic } = useUnfollowTopic();

  const regions = useMemo(() => regionsData?.regions ?? [], [regionsData]);

  const querySelectedCountries = useMemo(
    () => new Set(followedTopicsData?.values ?? []),
    [followedTopicsData]
  );

  const [localSelectedCountries, setLocalSelectedCountries] =
    useState<Set<string> | null>(null);

  const selectedCountries = localSelectedCountries ?? querySelectedCountries;

  const handleSelectionChange = (next: Set<string>) => {
    const previousSelection = selectedCountries;
    const toFollow = [...next].filter(code => !previousSelection.has(code));
    const toUnfollow = [...previousSelection].filter(code => !next.has(code));

    setLocalSelectedCountries(new Set(next));

    toFollow.forEach(code => {
      followTopic({ type: 'geography', code });
      trackFollowGeography(trackingSource, code);
    });

    toUnfollow.forEach(code => {
      unfollowTopic({ type: 'geography', code });
      trackUnfollowGeography(trackingSource, code);
    });
  };

  return (
    <div className={styles.root}>
      <FollowGeographiesSearchBar
        className={styles.searchBar}
        variant="drawer"
        regions={regions}
        selectedCountries={selectedCountries}
        onSelectionChange={handleSelectionChange}
        isOpen={isSearchOpen}
        onOpenChange={setIsSearchOpen}
      />
    </div>
  );
};
